import React from 'react';
import { Checkbox, Col, Form, Input, Radio, Row, Space, Typography } from 'antd';
import type { FormInstance } from 'antd';
import CheckAllButtons from '../CheckAllButtons';
import WeldMetaBlock from '../WeldMetaBlock';
import { SECTION_HEADER_WIDTH } from '../types';
import type { WeldMeta } from '../types';
import Button from '../../../../../../../../components/common/Button';

const { Text } = Typography;

export interface BcPanelProps {
  form: FormInstance;
  weldKey: string;
  isView: boolean;
  meta?: WeldMeta;
  onOpenHeatSelect: (field: 'heat_no_1' | 'heat_no_2') => void;
}

/** BC (Before Welding Check) detay paneli — JSX PipeInspectionForm'dan birebir taşındı.
 *  PipeHeatNoSelect modalı orkestratörde; panel yalnız aç callback'i alır.
 *  Heat inputları form store'a doğrudan bağlıdır (Space.Compact > Form.Item noStyle). */
export default function BcPanel({ form, weldKey, isView, meta, onOpenHeatSelect }: BcPanelProps) {
  return (
    <div>
      {/* Weld meta info from RFI (read-only) */}
      <WeldMetaBlock meta={meta} />
      <div style={{ padding: 4, backgroundColor: '#ffd27f', borderRadius: 4 }}>
        <Row gutter={[6, 4]}>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Heat Nos</Text>
              <Text>Heat No 1</Text>
              <Space.Compact>
                <Form.Item name={['results', weldKey, 'bc', 'heat_no_1']} noStyle>
                  <Input size="small" style={{ width: 160 }} disabled={isView} />
                </Form.Item>
                {!isView && (
                  <Button onMouseDown={(e) => e.preventDefault()} onClick={() => onOpenHeatSelect('heat_no_1')}>Select</Button>
                )}
              </Space.Compact>
              <Text>Heat No 2</Text>
              <Space.Compact>
                <Form.Item name={['results', weldKey, 'bc', 'heat_no_2']} noStyle>
                  <Input size="small" style={{ width: 160 }} disabled={isView} />
                </Form.Item>
                {!isView && (
                  <Button onMouseDown={(e) => e.preventDefault()} onClick={() => onOpenHeatSelect('heat_no_2')}>Select</Button>
                )}
              </Space.Compact>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Bevel</Text>
              <Text>Bevel Angle</Text>
              <Form.Item name={['results', weldKey, 'bc', 'bevel_angle']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 110 }} type="number" step="0.5" placeholder="degrees" disabled={isView} />
              </Form.Item>
              <Text>Root Face</Text>
              <Form.Item name={['results', weldKey, 'bc', 'root_face_mm']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 110 }} type="number" step="0.1" placeholder="mm" disabled={isView} />
              </Form.Item>
              <Text>Root Gap</Text>
              <Form.Item name={['results', weldKey, 'bc', 'root_gap_mm']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 110 }} type="number" step="0.1" placeholder="mm" disabled={isView} />
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Fit-Up</Text>
              <Text>Hi-Lo</Text>
              <Form.Item name={['results', weldKey, 'bc', 'fitup', 'hi_lo_mm']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 100 }} type="number" step="0.1" placeholder="mm" disabled={isView} />
              </Form.Item>
              <Form.Item name={['results', weldKey, 'bc', 'fitup', 'ovality_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Ovality Within Tolerance</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Prep</Text>
              <Form.Item name={['results', weldKey, 'bc', 'prep_method']} style={{ margin: 0 }}>
                <Radio.Group size="small" disabled={isView}>
                  <Radio value="machined">Machined</Radio>
                  <Radio value="flame">Flame cut</Radio>
                  <Radio value="ground">Ground</Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'bc', 'cleaning_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Bevel +25–50mm clean</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Surface</Text>
              <Form.Item name={['results', weldKey, 'bc', 'surface_ok', 'cracks']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Free of cracks</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'bc', 'surface_ok', 'laminations']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Free of laminations</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'bc', 'surface_ok', 'rust']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Free of rust</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'bc', 'surface_ok', 'paint_oil']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Free of paint/oil</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Tack</Text>
              <Form.Item name={['results', weldKey, 'bc', 'tack_welds_ok', 'size_spacing']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Size / spacing correct</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'bc', 'tack_welds_ok', 'defects_free']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Free of defects</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="center" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Other / Env.</Text>
              <Form.Item name={['results', weldKey, 'bc', 'consumables_env', 'electrode_batch_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Electrode/filler</Checkbox>
              </Form.Item>
              <Form.Item name={['results', weldKey, 'bc', 'consumables_env', 'preheat_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Preheat</Checkbox>
              </Form.Item>
              <Text>Preheat</Text>
              <Form.Item name={['results', weldKey, 'bc', 'consumables_env', 'preheat_temp']} style={{ margin: 0 }}>
                <Input size="small" style={{ width: 60 }} type="number" step="1" placeholder="°C" disabled={isView} />
              </Form.Item>
              <Form.Item name={['results', weldKey, 'bc', 'consumables_env', 'ambient_ok']} valuePropName="checked" style={{ margin: 0 }}>
                <Checkbox disabled={isView}>Ambient OK</Checkbox>
              </Form.Item>
            </Space>
          </Col>
          <Col xs={24}>
            <Space size={6} align="start" wrap>
              <Text strong style={{ display: 'inline-block', width: SECTION_HEADER_WIDTH, marginRight: 4 }}>Remarks</Text>
              <Form.Item name={["results", weldKey, "bc", "remarks"]} style={{ margin: 0, flex: 1 }}>
                <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} style={{ minWidth: 480 }} disabled={isView} />
              </Form.Item>
              {!isView && (
                <CheckAllButtons
                  onSetAll={(v) => {
                    const k = weldKey;
                    form.setFieldsValue({
                      results: {
                        [k]: {
                          bc: {
                            fitup: { ...(form.getFieldValue(['results', k, 'bc', 'fitup']) || {}), ovality_ok: v },
                            cleaning_ok: v,
                            surface_ok: { cracks: v, laminations: v, rust: v, paint_oil: v },
                            tack_welds_ok: { size_spacing: v, defects_free: v },
                            consumables_env: {
                              ...(form.getFieldValue(['results', k, 'bc', 'consumables_env']) || {}),
                              electrode_batch_ok: v,
                              preheat_ok: v,
                              ambient_ok: v,
                            },
                          },
                        },
                      },
                    });
                  }}
                />
              )}
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  );
}
